import { PropertyCard } from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { useFeaturedProperties } from "@/hooks/useFeaturedProperties";
import {
  useProvinces,
  useDistrictsByProvinceId,
  useCitiesByDistrictId,
} from "@/hooks/useLocations";
import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ListingType } from "@/lib/types"; // Import ListingType

interface FeaturedPropertiesProps {
  listingType?: ListingType[]; // Add listingType prop
}

// Feature: use react-query cache for propertyTypes & locations for reuse elsewhere.
const FeaturedProperties = ({ listingType }: FeaturedPropertiesProps) => {
  console.log("[FeaturedProperties] Received listingType prop:", listingType); // Add log
  const {
    data: properties = [],
    isLoading,
    isError,
  } = useFeaturedProperties({
    listingType: listingType, // Pass listingType inside an object
  });
  // Start fetch so it's cached for other home/search components:
  usePropertyTypes();
  useProvinces();
  useDistrictsByProvinceId(undefined);
  useCitiesByDistrictId(undefined);

  if (!isLoading && properties.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-realestate-gray">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10">
          <div>
            <h2 className="text-3xl font-bold text-realestate-darkblue mb-2">
              Featured Properties
            </h2>
            <p className="text-gray-600">
              Discover our handpicked selection of premium commercial
              opportunities
            </p>
          </div>
          <Link
            to="/properties"
            className="mt-4 md:mt-0 inline-flex items-center text-realestate-blue hover:text-realestate-darkblue transition-colors font-medium"
          >
            View All Properties
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md h-[400px] animate-pulse"
              >
                <div className="h-48 bg-gray-200 rounded-t-lg" />
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-4" />
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-full mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-full mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-4" />
                  <div className="h-10 bg-gray-200 rounded w-full mt-8" />
                </div>
              </div>
            ))
          ) : isError ? (
            <div className="col-span-3 text-center py-8">
              <p className="text-gray-500">
                Failed to load featured properties. Please try again later.
              </p>
            </div>
          ) : properties && properties.length > 0 ? (
            properties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))
          ) : null}
        </div>
        <div className="text-center mt-10">
          <Button
            className="bg-realestate-blue hover:bg-realestate-darkblue text-white px-8 py-3"
            asChild
          >
            <Link to="/properties">Explore All Properties</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProperties;
